import type { Point } from '../contract/types.js';

export type PathSegment =
  | { kind: 'move'; to: Point }
  | { kind: 'line'; to: Point }
  | { kind: 'curve'; c1: Point; c2: Point; to: Point }
  | { kind: 'close' };

export type CurveSegment = Extract<PathSegment, { kind: 'curve' }>;

/**
 * One PDF path operation may hold many disconnected sub-paths, and their
 * boundaries are not marked explicitly — a new `move` starts one.
 *
 * A single path dictionary has been observed holding a line, a rectangle and a
 * circle together. Mapping one path to one polyline merges unrelated shapes and
 * is the classic way this stage goes wrong.
 */
export function splitSubPaths(segments: PathSegment[]): PathSegment[][] {
  const out: PathSegment[][] = [];
  let current: PathSegment[] = [];
  for (const seg of segments) {
    if (seg.kind === 'move' && current.length > 0) {
      out.push(current);
      current = [];
    }
    current.push(seg);
  }
  if (current.length > 0) out.push(current);
  return out;
}

/** On-curve points, in order. Control points are excluded. */
export function subPathPoints(sub: PathSegment[]): Point[] {
  const pts: Point[] = [];
  for (const seg of sub) {
    if (seg.kind === 'move' || seg.kind === 'line' || seg.kind === 'curve') pts.push(seg.to);
  }
  return pts;
}

export function isClosed(sub: PathSegment[]): boolean {
  return sub.some((s) => s.kind === 'close');
}

/**
 * The points a bounding box must cover, curves included.
 *
 * {@link subPathPoints} returns only the ON-CURVE points — every `seg.to` — and a
 * cubic's shape lives in its control points. For a subpath that is a single curve
 * that leaves a chord: two endpoints, and a box of zero height whenever the curve
 * returns to the height it started at.
 *
 * That box is what an `unsupported_curve` issue reports and what the overlay
 * draws as the dashed rectangle telling a reviewer which geometry did not reach
 * the DXF. On fixture F01 the two freeform Beziers run from (100, 770) to
 * (280, 770) with control points 60 pt either side, so the marker was
 * `[100, 770, 280, 770]` — a flat line through the middle of an S-curve
 * 34.64 pt tall. **12.22 mm of geometry the marker did not cover**, on the one
 * thing whose entire job is to say where the unconverted shape is.
 *
 * Solved, not sampled. The control hull is a correct bound and a loose one — it
 * gives 710..830 for a curve occupying 752.7..787.3, a box three and a half times
 * too tall, which points at the wrong geometry as surely as a flat one. Sampling
 * is tight but never quite reaches the extremum: sixteen steps came 0.18 pt
 * short of it. A cubic's extrema are the roots of its derivative, which is a
 * quadratic, so they are available exactly — and exactly is also byte-stable,
 * which the artefact hashes depend on.
 */
export function curveHullPoints(sub: PathSegment[]): Point[] {
  const pts: Point[] = [];
  let cursor: Point | null = null;
  for (const seg of sub) {
    if (seg.kind === 'move' || seg.kind === 'line') {
      pts.push(seg.to);
      cursor = seg.to;
      continue;
    }
    if (seg.kind !== 'curve') continue;
    const from = cursor ?? seg.to;
    pts.push(from, seg.to);
    for (const t of cubicExtrema(from, seg.c1, seg.c2, seg.to)) {
      pts.push(cubicAt(from, seg.c1, seg.c2, seg.to, t));
    }
    cursor = seg.to;
  }
  return pts;
}

/** The cubic at `t`. */
function cubicAt(p0: Point, c1: Point, c2: Point, p3: Point, t: number): Point {
  const m = 1 - t;
  const a = m * m * m;
  const b = 3 * m * m * t;
  const c = 3 * m * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * c1.x + c * c2.x + d * p3.x,
    y: a * p0.y + b * c1.y + c * c2.y + d * p3.y,
  };
}

/**
 * The `t` in (0, 1) where the cubic turns, per axis.
 *
 * `B'(t) = 0` is a quadratic in each coordinate; a degenerate leading term makes
 * it linear, which is why that case is handled rather than divided by.
 */
function cubicExtrema(p0: Point, c1: Point, c2: Point, p3: Point): number[] {
  const ts: number[] = [];
  for (const k of ['x', 'y'] as const) {
    const a = -p0[k] + 3 * c1[k] - 3 * c2[k] + p3[k];
    const b = 2 * p0[k] - 4 * c1[k] + 2 * c2[k];
    const c = -p0[k] + c1[k];
    if (Math.abs(a) < 1e-12) {
      if (Math.abs(b) > 1e-12) ts.push(-c / b);
      continue;
    }
    const disc = b * b - 4 * a * c;
    if (disc < 0) continue;
    const root = Math.sqrt(disc);
    ts.push((-b + root) / (2 * a), (-b - root) / (2 * a));
  }
  return ts.filter((t) => t > 0 && t < 1);
}

export function bboxOf(points: Point[]): readonly [number, number, number, number] {
  if (points.length === 0) return [0, 0, 0, 0];
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const q of points) {
    if (q.x < minX) minX = q.x;
    if (q.y < minY) minY = q.y;
    if (q.x > maxX) maxX = q.x;
    if (q.y > maxY) maxY = q.y;
  }
  return [minX, minY, maxX, maxY];
}
